// Section 10 Tools panel (stream M3a): tools on hand, effective tools after durability, demand
// from the next season's assignments and the shortfall, the allocation policy, manual per-person
// pins, and per worker whether they had a tool last season and the output difference it made.
// Props only; mounted inside the Build tab.
import type { PersonId, ToolAllocationOption } from '../../sim/api';
import type { ToolsVM } from '../../viewModels/tools';
import { toolsText } from '../text/en';
import './tools.css';

interface Props {
  vm: ToolsVM;
  canEdit: boolean;
  jobLabel: (job: ToolsVM['workers'][number]['job']) => string;
  onPolicy: (option: ToolAllocationOption) => void;
  onPin: (personId: PersonId, has: boolean | null) => void;
}

function n1(x: number): string {
  const r = Math.round(x * 10) / 10;
  return Number.isInteger(r) ? r.toString() : r.toFixed(1);
}

export function ToolsPanel({ vm, canEdit, jobLabel, onPolicy, onPin }: Props) {
  const since = vm.seasonsWithoutToolmaker;
  return (
    <section class="tools" aria-labelledby="tools-title">
      <h3 id="tools-title">{toolsText.title}</h3>
      <dl class="tools__stats">
        <div>
          <dt>{toolsText.onHand}</dt>
          <dd class="num">{n1(vm.amount)}</dd>
        </div>
        <div>
          <dt>{toolsText.effective}</dt>
          <dd class="num">
            {n1(vm.effective)}{' '}
            <span class="hint">
              ({toolsText.durability} {Math.round(vm.durability * 100)}%)
            </span>
          </dd>
        </div>
        <div>
          <dt>{toolsText.demand}</dt>
          <dd class="num">{vm.demand}</dd>
        </div>
        <div>
          <dt>{toolsText.shortfall}</dt>
          <dd class="num">
            {vm.shortfall > 0 ? (
              <strong class="tools__short">{vm.shortfall}</strong>
            ) : (
              <span class="hint">{toolsText.noShortfall}</span>
            )}
          </dd>
        </div>
      </dl>
      {vm.lastSeason !== null && (
        <p class="hint">
          {toolsText.lastSeason(
            n1(vm.lastSeason.made),
            n1(vm.lastSeason.repaired),
            n1(vm.lastSeason.worn),
          )}
        </p>
      )}
      {since >= vm.warnAfter && (
        <p class="tools__warning" role="status">
          <strong>{toolsText.noToolmaker(since)}</strong>{' '}
          {since >= vm.decayAfter && toolsText.decaying}
        </p>
      )}
      <label class="field">
        <span class="field__label">{toolsText.policy}</span>
        <select
          value={vm.policy}
          disabled={!canEdit}
          onChange={(e) => onPolicy(e.currentTarget.value as ToolAllocationOption)}
        >
          {vm.options.map((o) => (
            <option key={o} value={o}>
              {toolsText.policies[o]}
            </option>
          ))}
        </select>
      </label>
      {vm.workers.length === 0 ? (
        <p class="hint">{toolsText.noWorkers}</p>
      ) : (
        <table class="table tools__workers">
          <thead>
            <tr>
              <th scope="col">{toolsText.colWorker}</th>
              <th scope="col">{toolsText.colJob}</th>
              <th scope="col">{toolsText.colHadTool}</th>
              <th scope="col">{toolsText.colDifference}</th>
              <th scope="col">{toolsText.colPin}</th>
            </tr>
          </thead>
          <tbody>
            {vm.workers.map((w) => (
              <tr key={w.id}>
                <th scope="row" class="name">
                  {w.name}
                </th>
                <td>{jobLabel(w.job)}</td>
                <td>
                  {w.hadTool === null
                    ? toolsText.notAtToolJob
                    : w.hadTool
                      ? toolsText.yes
                      : toolsText.no}
                </td>
                <td class="num">
                  {w.toolDifference === null
                    ? '-'
                    : w.hadTool === true
                      ? toolsText.withTool(n1(w.toolDifference))
                      : toolsText.withoutTool(n1(w.toolDifference))}
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`${toolsText.colPin}: ${w.name}`}
                    checked={w.pinned === true}
                    disabled={!canEdit || !w.wants}
                    onChange={(e) => onPin(w.id, e.currentTarget.checked)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!vm.manual && vm.workers.length > 0 && <p class="hint">{toolsText.pinHint}</p>}
    </section>
  );
}
